import {TrashIcon} from '@heroicons/react/20/solid';
import {CodeBracketSquareIcon, LinkSlashIcon} from '@heroicons/react/24/solid';
import React, {useState} from 'react';

import {
  TFirebaseMediaLibraryItem,
  TFirebaseMediaLibraryItemReady,
} from '@chancer/common/lib/interfaces/firestore/FirestoreClientInterfaces';
import {
  MediaLibraryItemStatus,
  TMediaEntry,
} from '@chancer/common/lib/interfaces/firestore/FirestoreInterfaces';
import {SparklesIcon} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import {useRecoilValue} from 'recoil';
import {isFullAdminSelector} from '../../../../recoil/FirestoreRecoil';
import {imageUrlToFileBlob} from '../../../../utils/ImageUtils';
import {Button, IconButton} from '../../../Button';
import {EditMedia} from '../../../components/Form/EditMedia';
import {TextareaInput} from '../../../components/Form/FormElements';
import {
  FormSection,
  FormSectionItem,
} from '../../../components/Form/FormSection';
import {CardListItem} from '../../../components/Layout/CardList';
import {SocialEmbed} from '../../../components/SocialEmbed';
import {FeedbackDialog} from '../../../components/Modals/FeedbackDialog';
import AddTag from './AddTag';
import {MediaChip} from './MediaChip';
import Tag from './Tag';

interface IProps {
  media: TFirebaseMediaLibraryItem;
  onCaptionSelected?: (caption: string) => void;
  onOpenGraphImageSelected?: (
    media: TFirebaseMediaLibraryItemReady,
    imageUrl: string,
  ) => void;
  onEmbedSelected?: (media: TFirebaseMediaLibraryItemReady) => void;
  onDelete?: (id: string) => void;
  onTagDelete?: (id: string | undefined, tag: string) => void;
  onTagAdd?: (id: string, tag: string) => void;
  onRegenerateCaption?: (media: TFirebaseMediaLibraryItemReady) => void;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
}

export const MediaListItem: React.FC<IProps> = (props) => {
  const isAdmin = useRecoilValue(isFullAdminSelector);
  const {media, onTagAdd, onTagDelete} = props;
  const [open, setOpen] = useState(false);
  const [customCropFile, setCustomCropFile] = useState<File | null>(null);
  const [overlayText, setOverlayText] = useState<string | null>(null);
  const [overlayTitle, setOverlayTitle] = useState('');

  const _decodeHtml = (html: string) => {
    var txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  const _handleCaptionSelected = (caption: string) => {
    if (props.onCaptionSelected) {
      props.onCaptionSelected(caption);
    }
  };

  const _handleEmbedSelected = () => {
    if (
      props.onEmbedSelected &&
      media.status === MediaLibraryItemStatus.READY
    ) {
      props.onEmbedSelected(media);
    }
  };

  const _handleOpenGraphImageSelected = (url: string) => {
    if (
      props.onOpenGraphImageSelected &&
      media.status === MediaLibraryItemStatus.READY
    ) {
      props.onOpenGraphImageSelected(media, url);
    }
  };

  const _handleCropOpenGraphImageSelected = async () => {
    if (customCropFile !== null) {
      setCustomCropFile(null);
      return;
    }
    if (media && media.status === MediaLibraryItemStatus.READY && media.image) {
      const file = await imageUrlToFileBlob(media.image, 'home-image.png');
      setCustomCropFile(file);
    }
  };

  const _onUpdateCroppedImage = (update: TMediaEntry) => {
    if (update.image?.url) {
      _handleOpenGraphImageSelected(update.image.url);
    }
  };

  return (
    <CardListItem key={media.id}>
      <div className="w-full">
        <div
          className="grid gap-3 lg:grid-cols-6 xl:grid-cols-12 w-full text-sm"
          onClick={() => setOpen(!open)}>
          {props.onToggleSelect && (
            <div
              className="col-span-1 flex items-center"
              onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                checked={props.isSelected ?? false}
                onChange={() =>
                  props.onToggleSelect && props.onToggleSelect(media.id)
                }
              />
            </div>
          )}
          <div className={props.onToggleSelect ? 'col-span-8' : 'col-span-9'}>
            <div className="flex">
              {media.status === MediaLibraryItemStatus.READY ? (
                <img src={media.icon} className="w-8 h-8 mr-4 mt-1" />
              ) : (
                <div className="w-8 h-8 mr-4 mt-1 bg-gray-50" />
              )}
              <div className="flex-1">
                {media.url}
                {media.status === MediaLibraryItemStatus.READY ? (
                  <div className="pt-2">{_decodeHtml(media.title ?? '')}</div>
                ) : (
                  ''
                )}
              </div>
            </div>
          </div>
          <div className="col-span-1">
            {media.status === MediaLibraryItemStatus.READY ? (
              media.image ? (
                <div
                  className="w-10 h-10 rounded-md"
                  style={{
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundImage: `url(${media.image})`,
                  }}
                />
              ) : (
                <div className="w-10 h-10 p-2">
                  <LinkSlashIcon className="text-gray-300" />
                </div>
              )
            ) : (
              ''
            )}
          </div>
          <div className="col-span-1">
            {media.status === MediaLibraryItemStatus.READY ? (
              media.socialEmbed ? (
                <div className="w-10 h-10">
                  <CodeBracketSquareIcon className="text-blue-600" />
                </div>
              ) : (
                <div className="w-10 h-10 p-2">
                  <LinkSlashIcon className="text-gray-300" />
                </div>
              )
            ) : (
              ''
            )}
          </div>
          <div className="col-span-1">
            <div className="flex md:justify-end items-center">
              <div className="mr-1">
                <MediaChip media={media} />
              </div>
              {props.onDelete !== undefined && (
                <IconButton
                  size="xsmall"
                  onClick={() => props.onDelete && props.onDelete(media.id)}>
                  <TrashIcon className="text-blue-600" />
                </IconButton>
              )}
            </div>
          </div>
        </div>
        {open ? (
          <div className="w-full border rounded-md mt-4 p-4 text-sm">
            {media.status === MediaLibraryItemStatus.READY &&
            (media.tags?.length || onTagAdd) ? (
              <FormSectionItem columnSpan="6">
                <div className="flex-1 pt-4 flex flex-col items-start">
                  {props.onRegenerateCaption && (
                    <div className="flex flex-wrap gap-2 mb-2 -mt-2">
                      <Button
                        Icon={SparklesIcon}
                        label="Regenerate Caption"
                        onClick={() =>
                          props.onRegenerateCaption &&
                          props.onRegenerateCaption(media)
                        }
                      />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {media.tags?.map((tag, i) => (
                      <Tag
                        key={i.toString()}
                        tag={tag}
                        media={{id: media.id}}
                        onClick={onTagDelete}
                      />
                    ))}
                    {isAdmin && onTagAdd && (
                      <AddTag
                        offset={true}
                        onTagAdd={(tag) => onTagAdd(media.id, tag)}
                      />
                    )}
                  </div>
                </div>
              </FormSectionItem>
            ) : null}
            {media.status === MediaLibraryItemStatus.READY ? (
              <FormSection>
                {customCropFile ? (
                  <FormSectionItem columnSpan="6">
                    <Button
                      label="Close Cropper"
                      category="secondarycolor"
                      disabled={media.image === undefined}
                      onClick={_handleCropOpenGraphImageSelected}
                    />
                    <EditMedia
                      value={{}}
                      disabled={false}
                      dimensions={[540, 540]}
                      selectedFile={customCropFile}
                      setIsUpdating={() => {}}
                      onUpdate={_onUpdateCroppedImage}
                    />
                  </FormSectionItem>
                ) : (
                  <>
                    <FormSectionItem columnSpan="3">
                      <div className="flex-1 border-2 border-dashed rounded-md p-2 pt-4 flex flex-col items-center">
                        <div className="flex w-full items-center -mt-2">
                          <div className="font-semibold pl-2 flex-1">
                            Open Graph Image
                          </div>
                          {props.onOpenGraphImageSelected && (
                            <Button
                              label="Custom Crop"
                              category="secondarycolor"
                              disabled={media.image === undefined}
                              onClick={_handleCropOpenGraphImageSelected}
                            />
                          )}
                        </div>

                        {media.imageSquare ? (
                          <MediaListOpenGraphImage
                            url={media.imageSquare}
                            onSelect={
                              props.onOpenGraphImageSelected
                                ? _handleOpenGraphImageSelected
                                : undefined
                            }
                          />
                        ) : null}
                        {media.imagePortrait ? (
                          <MediaListOpenGraphImage
                            url={media.imagePortrait}
                            onSelect={
                              props.onOpenGraphImageSelected
                                ? _handleOpenGraphImageSelected
                                : undefined
                            }
                          />
                        ) : null}
                        {media.imageContained ? (
                          <MediaListOpenGraphImage
                            url={media.imageContained}
                            onSelect={
                              props.onOpenGraphImageSelected
                                ? _handleOpenGraphImageSelected
                                : undefined
                            }
                          />
                        ) : null}
                      </div>
                    </FormSectionItem>
                    <FormSectionItem columnSpan="3">
                      <div
                        className="flex-1 border-2 border-dashed rounded-md p-2 pt-4 flex flex-col items-center"
                        style={{aspectRatio: 1 / 1.2}}>
                        <div className="flex w-full items-center -mt-2">
                          <div className="font-semibold pl-2 flex-1">Embed</div>
                          <Button
                            label="Use"
                            category="secondarycolor"
                            disabled={
                              props.onEmbedSelected === undefined ||
                              media.socialEmbed === undefined
                            }
                            onClick={_handleEmbedSelected}
                          />
                        </div>
                        {media.socialEmbed ? (
                          <SocialEmbed
                            embed={media.socialEmbed}
                            width="100%"
                            height="100%"
                          />
                        ) : null}
                      </div>
                    </FormSectionItem>
                  </>
                )}
                {media.captions?.map((caption, i) => (
                  <FormSectionItem columnSpan="2">
                    <TextareaInput
                      key={i}
                      label={`Caption ${i + 1}`}
                      inputProps={{
                        rows: 5,
                        value: caption,
                      }}
                    />
                    <Button
                      className="-mt-5"
                      label="Use"
                      category="secondarycolor"
                      onClick={() => _handleCaptionSelected(caption)}
                      disabled={props.onCaptionSelected === undefined}
                    />
                  </FormSectionItem>
                ))}
                {media.status === MediaLibraryItemStatus.READY &&
                media.context?.readableText ? (
                  <FormSectionItem columnSpan="6">
                    <TextareaInput
                      label="Readable Text / Transcript"
                      inputProps={{
                        rows: 7,
                        value: media.context.readableText,
                        readOnly: true,
                        style: {overflowY: 'auto'},
                      }}
                    />
                    <Button
                      className="-mt-5"
                      label="Expand"
                      category="secondarycolor"
                      onClick={() => {
                        setOverlayTitle('Readable Text / Transcript');
                        setOverlayText(
                          (media as TFirebaseMediaLibraryItemReady).context!
                            .readableText!,
                        );
                      }}
                    />
                  </FormSectionItem>
                ) : null}
                {media.status === MediaLibraryItemStatus.READY &&
                media.description ? (
                  <FormSectionItem columnSpan="6">
                    <TextareaInput
                      label="Description"
                      inputProps={{
                        rows: 7,
                        value: media.description,
                        readOnly: true,
                        style: {overflowY: 'auto'},
                      }}
                    />
                    <Button
                      className="-mt-5"
                      label="Expand"
                      category="secondarycolor"
                      onClick={() => {
                        setOverlayTitle('Description');
                        setOverlayText(media.description!);
                      }}
                    />
                  </FormSectionItem>
                ) : null}
              </FormSection>
            ) : media.status === MediaLibraryItemStatus.PENDING ? (
              <div>Link is still processing</div>
            ) : media.status === MediaLibraryItemStatus.ERROR ? (
              <div>Error</div>
            ) : null}
          </div>
        ) : null}
      </div>
      <FeedbackDialog
        open={overlayText !== null}
        title={overlayTitle}
        message={
          <pre className="whitespace-pre-wrap text-sm">{overlayText ?? ''}</pre>
        }
        onClose={() => setOverlayText(null)}
      />
    </CardListItem>
  );
};

interface IMediaListOpenGraphImageProps {
  url: string;
  onSelect?: (url: string) => void;
}
const MediaListOpenGraphImage = (props: IMediaListOpenGraphImageProps) => {
  return (
    <div
      className={classNames(
        'overflow-hidden rounded-md border-4 border-gray-50',
        props.onSelect && 'hover:border-blue-400 p-1 cursor-pointer',
      )}
      onClick={() => props.onSelect && props.onSelect(props.url)}>
      <img src={props.url} className="max-h-60" />
    </div>
  );
};
